import { FC, useEffect, useState } from 'react';
import './PendingToConfirmedModalLS.scss';
import {
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  TlogisticsPaymentTypes
} from '../../../../../types/services';
import LogisticsServiceTrelloCard from '../../LogisticsServiceTrelloCard/LogisticsServiceTrelloCard';
import { NudosButton, NudosFileInput, NudosTextInput } from '../../../../../components/NudosComponents';
import NudosDatePicker from '../../../../../components/NudosComponents/NudosDatePicker';
import { TchangeLogisticServiceStatusCallback } from '../LogisticsServicesTrelloModals';
import { getNudosData } from '../../../../../utils/getNudosData';
import { isFileValid } from '../../../../../utils/validateFiles';
import { isValidLink } from '../../../../../utils/formValidations';
import {
  METHODS_ENABLE_PAYMENT_LINK_SL,
  PAYMENT_TYPE_OPTIONS,
  PAYMENT_TYPE_OPTIONS_TEXT,
  PAYMENT_TYPES
} from '../../../../../types/orders';
import BordTooltip from '../../../../../components/BordTooltip/BordTooltip';
import { paymentDateBlockedMessage } from '../../../../../utils/logisticServices.utils';

interface IPendingToConfirmedModalLSContent {
  onlyItemCard: IlogisticServiceTrelloCardData;
  logisticServiceDetails?: IlogisticServiceDetails;
  changeLogisticServiceStatusCallback: TchangeLogisticServiceStatusCallback;
  loadingChangeLSStatus?: boolean;
  closeModal?: () => void;
}

type TPaymentType = 'counted' | 'paymentAgreement' | 'care' | undefined;

const PendingToConfirmedModalLSContent: FC<IPendingToConfirmedModalLSContent> = ({
  onlyItemCard,
  changeLogisticServiceStatusCallback,
  logisticServiceDetails,
  loadingChangeLSStatus,
  closeModal
}: IPendingToConfirmedModalLSContent) => {
  const nudosData = getNudosData();
  const enablePaymentLink = METHODS_ENABLE_PAYMENT_LINK_SL.includes(
    onlyItemCard?.payments?.paymentMethod as PAYMENT_TYPES
  );

  const [paymentType, setPaymetType] = useState<TPaymentType>('counted');
  const [enabledNudosCare, setEnabledNudosCare] = useState<boolean>(false);

  const [documentPaymentFile, setDocumentPaymentFile] = useState<File>();
  const [temporalUrlPaymentFile, setTemporalUrlPaymentFile] = useState<string>();

  const [documentInvoiceFile, setDocumentInvoiceFile] = useState<File>();
  const [temporalUrlInvoiceFile, setTemporalUrlInvoiceFile] = useState<string>();

  const [datePayment, setDatePayment] = useState<string>();
  const [paymentLink, setPaymentLink] = useState<string>();
  const [invoiceNumber, setInvoiceNumber] = useState<string>();

  const linkError = paymentLink && !isValidLink(paymentLink) ? 'Link inválido' : '';
  const countedPaymentType = paymentType === PAYMENT_TYPE_OPTIONS.COUNTED;
  const paymentAgreementPaymentType = paymentType === PAYMENT_TYPE_OPTIONS.PAYMENT_AGREEMENT;
  const disablePaymentAgreement = !onlyItemCard?.payments?.paymentMethod?.toLowerCase()?.includes('transf');

  const tooltipMessage = disablePaymentAgreement
    ? paymentDateBlockedMessage({ customClassName: 'alertTooltipPaymentAgreementLogisticsService' })
    : undefined;

  const disabledButton = () => {
    const renderPaymentLinkValidation = enablePaymentLink ? !paymentLink || !isValidLink(paymentLink) : false;

    if (!paymentType) return true;
    if (paymentType === 'counted' && !documentPaymentFile && !temporalUrlPaymentFile) return true;
    if (paymentType === 'paymentAgreement' && (!datePayment || renderPaymentLinkValidation)) return true;
    return false;
  };

  const handlePaymentFile = (paymentFile: File) => {
    const isValid = isFileValid(
      paymentFile,
      ['application/pdf', 'image/jpeg'],
      5,
      'El documento debe estar en formato PDF o JPG',
      'El documento no debe superar los 5MB'
    );

    if (isValid) {
      setDocumentPaymentFile(paymentFile);
      const base64File: string = URL.createObjectURL(paymentFile);
      if (base64File) {
        setTemporalUrlPaymentFile(base64File);
      }
    }
  };

  const handleInvoiceFile = (invoiceFile: File) => {
    const isValid = isFileValid(
      invoiceFile,
      ['application/pdf', 'image/jpeg'],
      5,
      'El documento debe estar en formato PDF o JPG',
      'El documento no debe superar los 5MB'
    );
    if (isValid) {
      setDocumentInvoiceFile(invoiceFile);
      const base64File: string = URL.createObjectURL(invoiceFile);
      if (base64File) {
        setTemporalUrlInvoiceFile(base64File);
      }
    }
  };

  const openUrl = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const updateStateLS = () => {
    const paymentTypeFormat =
      paymentType === 'counted' ? 'Cash' : paymentType === 'paymentAgreement' ? 'Agreement' : 'Care';
    const bodyChangeStatus = {
      status: 'confirmado',
      userId: Number(nudosData?.userData?.id),
      confirm: {
        paymentType: paymentTypeFormat as TlogisticsPaymentTypes,
        paymentLink: paymentType === 'paymentAgreement' && !!paymentLink ? paymentLink : undefined,
        paymentAgreementDate: paymentType === 'paymentAgreement' && !!datePayment ? datePayment : undefined,
        invoiceNumber:
          (paymentType === 'paymentAgreement' || paymentType === 'counted') && !!invoiceNumber
            ? Number(invoiceNumber || 0)
            : undefined
      }
    };

    const paymentFile = documentPaymentFile || undefined;
    const invoiceFile = paymentType && !!documentInvoiceFile ? documentInvoiceFile : undefined;
    return changeLogisticServiceStatusCallback(bodyChangeStatus, closeModal, paymentFile, invoiceFile);
  };

  const checkNudosCare = () => {
    if (logisticServiceDetails?.paymentType === 'Care') {
      setEnabledNudosCare(true);
      setPaymetType('care');
    } else {
      setEnabledNudosCare(false);
    }
  };
  const initialValues = () => {
    if (logisticServiceDetails?.invoiceUrl) {
      setTemporalUrlInvoiceFile(logisticServiceDetails?.invoiceUrl);
    }
    if (logisticServiceDetails?.proofOfPaymentUrl) {
      setTemporalUrlPaymentFile(logisticServiceDetails?.proofOfPaymentUrl);
      setPaymetType('counted');
    }
    if (logisticServiceDetails?.paymentLink) {
      setPaymentLink(logisticServiceDetails?.paymentLink);
    }
  };

  const formatPaymentType = () => {
    if (countedPaymentType) return PAYMENT_TYPE_OPTIONS_TEXT.COUNTED;
    if (paymentAgreementPaymentType) return PAYMENT_TYPE_OPTIONS_TEXT.PAYMENT_AGREEMENT;
    return '';
  };

  useEffect(() => {
    checkNudosCare();
    initialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticServiceDetails]);

  return (
    <div id="pendingToConfirmedModalLS">
      <div className="boxPending">
        <div className="bodyPendingModal">
          <div className="titlePending">Estás a punto de actualizar el estado del siguiente servicio:</div>
          <div className="containerCard">
            <div className="onlyCard">
              <LogisticsServiceTrelloCard cardStatusColumnClassname="noMargin" logisticServiceData={onlyItemCard} />
            </div>
          </div>
          <div className="limitsItemsModal">
            <div className="boxPaymentTypeSection">
              <div className="paymentTypeSection">
                <div className="paymentTitle">
                  <span className="font-bold">Tipo de pago - </span>
                  <span>{formatPaymentType()}</span>
                  <span className="ml-4">({onlyItemCard?.payments?.paymentMethod ?? ''})</span>
                </div>
                <div className="optionPaymentList">
                  <div className="selectOptionBox mr-8">
                    <div className="checkContainer">
                      {paymentType === 'counted' ? (
                        <div className="checkOption">
                          <div
                            className="checkOrange"
                            onClick={() => {
                              setPaymetType(undefined);
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className="noCheck"
                          onClick={() => {
                            setPaymetType('counted');
                          }}
                        />
                      )}
                    </div>
                    <div className="textContainer">Al contado</div>
                  </div>
                  <div className="selectOptionBox mr-8">
                    <BordTooltip position="bottom" message={tooltipMessage}>
                      <div className="checkContainer">
                        {paymentType === 'paymentAgreement' ? (
                          <div
                            className="checkOption"
                            onClick={() => {
                              setPaymetType(undefined);
                            }}
                          >
                            <div className="checkOrange" />
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              if (disablePaymentAgreement) return;
                              setPaymetType('paymentAgreement');
                            }}
                            className={`noCheck ${disablePaymentAgreement ? 'disabledPaymentAgreementCheck' : ''}`}
                          />
                        )}
                      </div>
                      <div className={`textContainer ${disablePaymentAgreement ? 'disabledPaymentAgreementText' : ''}`}>
                        Acuerdo de pago
                      </div>
                    </BordTooltip>
                  </div>
                  {enabledNudosCare && (
                    <div className="selectOptionBox">
                      <div className="checkContainer">
                        {paymentType === 'care' ? (
                          <div
                            className="checkOption"
                            onClick={() => {
                              setPaymetType(undefined);
                            }}
                          >
                            <div className="checkOrange" />
                          </div>
                        ) : (
                          <div
                            className="noCheck"
                            style={{ cursor: !enabledNudosCare ? 'not-allowed' : '' }}
                            onClick={() => {
                              if (enabledNudosCare) {
                                setPaymetType('care');
                              }
                            }}
                          />
                        )}
                      </div>
                      <div className="textContainer" style={{ color: !enabledNudosCare ? '#999999' : '' }}>
                        Nudos Care
                      </div>
                    </div>
                  )}
                </div>
                {paymentType === 'counted' && (
                  <>
                    <div className="containerPaymentClientBox">
                      <NudosFileInput
                        color="orange"
                        specialFill
                        customHeight="30px"
                        customWidth="127px"
                        buttonText="Pago cliente"
                        isFilled={!!documentPaymentFile || !!temporalUrlPaymentFile}
                        uploadedFileHandler={e => {
                          if (e) {
                            handlePaymentFile(e);
                          }
                        }}
                      />
                    </div>
                    <div className="previewPayment">
                      <div className="previewContainer">
                        {!!temporalUrlPaymentFile ? (
                          <div
                            className="paymentText paymentBlue"
                            onClick={() => {
                              openUrl(temporalUrlPaymentFile);
                            }}
                          >
                            Ver pago
                          </div>
                        ) : (
                          <div className="paymentText">Ver pago </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {paymentType === 'paymentAgreement' && (
                  <>
                    <div className="paymentAgreementContainer">
                      <div className="containerInputs">
                        {enablePaymentLink && (
                          <div className="paymentLinkBox">
                            <NudosTextInput
                              label="Link de pago"
                              handleChange={e => setPaymentLink(e)}
                              isFilled={!!paymentLink}
                              errorText={linkError}
                              defaultValue={paymentLink || ''}
                              placeholder="Ingresa el link de pago"
                            />
                          </div>
                        )}
                        <div className="datePickerBox">
                          <NudosDatePicker
                            label="Fecha de compromiso de pago"
                            componentSize="small"
                            isFilled={!!datePayment}
                            updateCurrentValueCallback={e => {
                              if (e) {
                                setDatePayment(e);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {paymentType === 'care' && (
                  <div className="carePaymentType">
                    <div className="boxNudosCareText">
                      <div className="textGreenNudosCare">Cubierto con Nudos Care</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="boxInvoiceSection">
              <div className="invoiceSection">
                <div className="invoiceTitle">
                  <span className="font-bold">Factura</span> - (opcional)
                </div>
                {(paymentType === 'paymentAgreement' || paymentType === 'counted') && (
                  <div className="numberInvoiceContainer">
                    <div className="invoiceBox">
                      <NudosTextInput
                        label="N° de Factura (opcional)"
                        inputHeight={40}
                        isFilled={!!invoiceNumber}
                        handleChange={e => {
                          setInvoiceNumber(e);
                        }}
                        placeholder="Escribe el número de factura"
                      />
                    </div>
                  </div>
                )}
                <div className="containerInvoiceBox">
                  <NudosFileInput
                    color="orange"
                    specialFill
                    isFilled={!!documentInvoiceFile || !!temporalUrlInvoiceFile}
                    customHeight="30px"
                    customWidth="127px"
                    buttonText="Factura"
                    uploadedFileHandler={e => {
                      if (e) {
                        handleInvoiceFile(e);
                      }
                    }}
                  />
                </div>
                <div className="previewInvoice">
                  <div className="previewContainer">
                    {temporalUrlInvoiceFile ? (
                      <div
                        className="paymentText paymentBlue"
                        onClick={() => {
                          openUrl(temporalUrlInvoiceFile);
                        }}
                      >
                        Ver factura{' '}
                      </div>
                    ) : (
                      <div className="paymentText">Ver factura</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`floatButtonConfirm ${paymentType === 'paymentAgreement' ? 'shadowConfirmButton' : ''}`}>
          <div className="containerStateButton">
            <div className="containerStateButton">
              <NudosButton
                isButtonLoading={loadingChangeLSStatus}
                isButtonDisabled={disabledButton() || loadingChangeLSStatus}
                customClassName="updateStateButton"
                buttonText="Guardar"
                handleClick={() => {
                  updateStateLS();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingToConfirmedModalLSContent;
